import type { StaticDatabaseHandle } from "@-/dev.improve.haniverse.database.static";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import { and, asc, count, eq, inArray, isNotNull, sql } from "@-/com.npmjs.drizzle-orm";

import type { HskLevelKey, HskVersionKey } from "#hsk-grade-word-lists";

/** The grapheme-PRC level column for each HSK version (character-level membership). */
const HSK_CHAR_LEVEL_COLUMNS = {
  hsk2: staticSchema.graphemePrcData.minHsk2010Level,
  hsk2026: staticSchema.graphemePrcData.minHsk2026Level,
  hsk3: staticSchema.graphemePrcData.minHsk2021Level,
} as const satisfies Record<HskVersionKey, typeof staticSchema.graphemePrcData.minHsk2010Level>;

/**
 * The HSK level/index column pair on `wordPrcData` for each HSK version. The level column holds
 * the 1-9 level (7-9 collapse to `7`), the index column holds the learning-order position used to
 * reproduce the original package array order.
 */
const HSK_WORD_COLUMNS = {
  hsk2: {
    index: staticSchema.wordPrcData.hsk2010Index,
    level: staticSchema.wordPrcData.hsk2010Level,
  },
  hsk2026: {
    index: staticSchema.wordPrcData.hsk2026Index,
    level: staticSchema.wordPrcData.hsk2026Level,
  },
  hsk3: {
    index: staticSchema.wordPrcData.hsk2021Index,
    level: staticSchema.wordPrcData.hsk2021Level,
  },
} as const satisfies Record<
  HskVersionKey,
  {
    index: typeof staticSchema.wordPrcData.hsk2010Index;
    level: typeof staticSchema.wordPrcData.hsk2010Level;
  }
>;

/**
 * The static DB stores HSK 7-9 under level `7`; the runtime represents that band as `79`.
 * Map the runtime level key to its stored value.
 */
function toStoredLevel(level: HskLevelKey): number {
  return level === 79 ? 7 : level;
}

/**
 * Words for one HSK level, in learning order (matching the original HSK package array order).
 *
 * Replaces the JSON-backed `getHskWordsForLevel` so the raw HSK word lists no longer need to be
 * bundled — the data is read from the shipped static DB instead.
 */
export async function getHskWordsForLevelFromDb(
  db: StaticDatabaseHandle,
  hskVersion: HskVersionKey,
  level: HskLevelKey,
): Promise<string[]> {
  const columns = HSK_WORD_COLUMNS[hskVersion];
  const rows = await db
    .select({ word: staticSchema.word.word })
    .from(staticSchema.wordPrcData)
    .innerJoin(staticSchema.word, eq(staticSchema.word.id, staticSchema.wordPrcData.wordId))
    .where(eq(columns.level, toStoredLevel(level)))
    .orderBy(asc(columns.index));
  return rows.map((row) => row.word);
}

/** Read several HSK bands in one round trip, preserving learning order within each band. */
export async function getHskWordsByLevelFromDb(
  db: StaticDatabaseHandle,
  hskVersion: HskVersionKey,
  levels: readonly HskLevelKey[],
): Promise<ReadonlyMap<HskLevelKey, readonly string[]>> {
  const columns = HSK_WORD_COLUMNS[hskVersion];
  const rows = await db
    .select({ level: columns.level, word: staticSchema.word.word })
    .from(staticSchema.wordPrcData)
    .innerJoin(staticSchema.word, eq(staticSchema.word.id, staticSchema.wordPrcData.wordId))
    .where(
      inArray(
        columns.level,
        levels.map((level) => toStoredLevel(level)),
      ),
    )
    .orderBy(asc(columns.level), asc(columns.index));
  const byLevel = new Map<HskLevelKey, string[]>(levels.map((level) => [level, []]));
  const keyByStoredLevel = new Map(levels.map((level) => [toStoredLevel(level), level]));
  for (const row of rows) {
    const level = row.level === null ? undefined : keyByStoredLevel.get(row.level);
    if (level !== undefined) {
      byLevel.get(level)?.push(row.word);
    }
  }
  return byLevel;
}

/**
 * The deduplicated set of every word across all levels of the given HSK version. Order is not
 * meaningful here — callers that need ordering should use {@link getHskWordsForLevelFromDb}.
 */
export async function getHskWordSetFromDb(
  db: StaticDatabaseHandle,
  hskVersion: HskVersionKey,
): Promise<Set<string>> {
  const columns = HSK_WORD_COLUMNS[hskVersion];
  const rows = await db
    .selectDistinct({ word: staticSchema.word.word })
    .from(staticSchema.wordPrcData)
    .innerJoin(staticSchema.word, eq(staticSchema.word.id, staticSchema.wordPrcData.wordId))
    .where(isNotNull(columns.level));
  return new Set(rows.map((row) => row.word));
}

/**
 * Each given word form's stored HSK level (`1`-`7`) in the version's lists; words outside them
 * are absent. A plain `select` (no `distinct`), so the worker transport can split the `in (…)`
 * past D1's bound-parameter cap; a form stored on several `word` rows keeps its lowest level.
 */
export async function getHskWordLevelsFromDb(
  db: StaticDatabaseHandle,
  hskVersion: HskVersionKey,
  words: readonly string[],
): Promise<ReadonlyMap<string, number>> {
  const levelColumn = HSK_WORD_COLUMNS[hskVersion].level;
  const rows = await db
    .select({ level: levelColumn, word: staticSchema.word.word })
    .from(staticSchema.wordPrcData)
    .innerJoin(staticSchema.word, eq(staticSchema.word.id, staticSchema.wordPrcData.wordId))
    .where(and(inArray(staticSchema.word.word, [...words]), isNotNull(levelColumn)));
  const levelByWord = new Map<string, number>();
  for (const row of rows) {
    if (row.level === null) {
      continue;
    }
    const existing = levelByWord.get(row.word);
    if (existing === undefined || row.level < existing) {
      levelByWord.set(row.word, row.level);
    }
  }
  return levelByWord;
}

/** Map of stored HSK level (`1`-`7`) → row count. */
export type HskLevelCounts = Map<number, number>;

/** Character counts per stored HSK level for the given version (from `graphemePrcData`). */
export async function getHskCharacterCountsByLevel(
  db: StaticDatabaseHandle,
  hskVersion: HskVersionKey,
): Promise<HskLevelCounts> {
  const levelColumn = HSK_CHAR_LEVEL_COLUMNS[hskVersion];
  const rows = await db
    .select({ count: count(), level: levelColumn })
    .from(staticSchema.graphemePrcData)
    .where(isNotNull(levelColumn))
    .groupBy(levelColumn);
  return new Map(rows.map((row) => [row.level ?? 0, row.count] as const));
}

/** Word counts per stored HSK level for the given version (from `wordPrcData`). */
export async function getHskWordCountsByLevel(
  db: StaticDatabaseHandle,
  hskVersion: HskVersionKey,
): Promise<HskLevelCounts> {
  const levelColumn = HSK_WORD_COLUMNS[hskVersion].level;
  // One row per distinct word form per level, mirroring the JSON list's per-word-form counting.
  const rows = await db
    .select({
      count: sql<number>`COUNT(DISTINCT ${staticSchema.word.word})`,
      level: levelColumn,
    })
    .from(staticSchema.wordPrcData)
    .innerJoin(staticSchema.word, eq(staticSchema.word.id, staticSchema.wordPrcData.wordId))
    .where(isNotNull(levelColumn))
    .groupBy(levelColumn);
  return new Map(rows.map((row) => [row.level ?? 0, row.count] as const));
}
